import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Compass, Loader2, Printer } from 'lucide-react';
import {
  LEVEL_TONE, createBureaucracyReview, downloadPocketGuidePdf, getAdoption, getMaturity,
  getMethodContent, getMethodDepartments, getRhythm, getToday, setAdoption, setRhythm,
} from './api';
import { AlignTab, IntegrateTab, KnowTab, NavigateTab, RespondTab, todayIso } from './MoveTabs';
import {
  BrochureLibrary, FailureSignalsCard, LaunchCard, LearningTab, ManagementTab, MaturityCard, MetricsTab,
} from './MethodExtras';

const TABS = [
  ['today', 'Today'], ['learning', 'Learning'], ['know', 'Know'], ['align', 'Align'], ['navigate', 'Navigate'],
  ['respond', 'Respond'], ['integrate', 'Integrate'], ['rhythm', 'Rhythm'],
  ['management', 'Management'], ['metrics', 'Metrics'], ['adoption', 'Adoption'], ['library', 'Library'],
];

const TodayTab = ({ data, content, onGo }) => {
  if (!data) return null;
  const c = data.counts;
  return (
    <div className="space-y-5" data-testid="method-today">
      <Card className={`border ${LEVEL_TONE[data.worst_level]}`}>
        <CardContent className="p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-80">Condition now</p>
          <p className="mt-1 font-display text-3xl font-bold uppercase" data-testid="method-today-level">{data.worst_level}</p>
          {data.next_action ? (
            <div className="mt-4 rounded-lg border border-current/20 bg-background/60 p-3" data-testid="method-today-next">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Next action · move {data.next_action.move}</p>
              <p className="mt-0.5 text-sm font-medium text-foreground">{data.next_action.what}</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => onGo(data.next_action.move)} data-testid="method-today-go">
                Go to {data.next_action.move}
              </Button>
            </div>
          ) : (
            <p className="mt-3 text-sm opacity-80">The loop is complete for today — continue and observe.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {[
          ['Conditions set', c.conditions, 'method-count-conditions'],
          ['Open abnormalities', c.open_responses, 'method-count-responses'],
          ['Containments expired', c.containments_expiring, 'method-count-containments'],
          ['Verifications due', c.verifications_due, 'method-count-verifications'],
          ['Management findings', c.mgmt_findings, 'method-count-mgmt'],
          ['Rhythm done', `${c.rhythm_done}/${c.rhythm_total}`, 'method-count-rhythm'],
        ].map(([label, value, tid]) => (
          <div key={label} className="rounded-xl border border-muted/60 bg-card p-4" data-testid={tid}>
            <p className="text-xl font-bold tabular-nums text-foreground">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {data.forecast?.reported_backlog > 0 && (
        <Card data-testid="method-today-forecast">
          <CardHeader><CardTitle className="text-sm">What can actually move</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Reported backlog', data.forecast.reported_backlog, 'method-today-reported'],
              ['Can move now', data.forecast.movable_units, 'method-today-movable'],
              ['Blocked or queued', data.forecast.held_units, 'method-today-held'],
              ['Overstated by', `${data.forecast.overstatement_percent}%`, 'method-today-overstated'],
            ].map(([label, value, tid]) => (
              <div key={label} data-testid={tid}>
                <p className="text-lg font-bold tabular-nums text-foreground">{value}</p>
                <p className="text-[11px] text-muted-foreground">{label}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-sm">Board architecture — what the control board must show</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-3">
          {content.board_zones.map((z) => (
            <div key={z.zone} className="rounded-lg border border-muted/60 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{z.zone} · {z.name}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{z.contains}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Model board — what each zone must cause</CardTitle>
          <p className="text-xs text-muted-foreground">If a zone does not drive a decision, it is a wall-mounted archive.</p>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-3">
          {content.model_board.map((z) => (
            <div key={z.zone} className="rounded-lg border border-muted/60 p-3" data-testid={`method-model-${z.zone.toLowerCase()}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{z.zone}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{z.shows}</p>
              <p className="mt-0.5 text-[11px] text-foreground">Decision: {z.decision}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const RhythmTab = ({ content, date, deptId = '', onChanged }) => {
  const [checks, setChecks] = useState([]);
  const load = useCallback(async () => setChecks((await getRhythm(date, deptId)).checks), [date, deptId]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (key, done) => {
    try { await setRhythm({ date, department_id: deptId, checkpoint: key, done }); await load(); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-5" data-testid="method-rhythm">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily operating rhythm · {date}</CardTitle>
          <p className="text-xs text-muted-foreground">The moments in a shift where control is won or lost. Each moment carries all five moves.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.rhythm.map((m) => {
            const done = checks.find((c) => c.checkpoint === m.key)?.done;
            return (
              <div key={m.key} className={`rounded-lg border p-3 ${done ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-muted/60'}`} data-testid={`method-rhythm-${m.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">{m.moment}</span>
                  <Button size="sm" variant={done ? 'outline' : 'default'} onClick={() => toggle(m.key, !done)} data-testid={`method-rhythm-toggle-${m.key}`}>
                    {done ? 'Done' : 'Mark done'}
                  </Button>
                </div>
                <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-4">
                  <p><span className="font-bold text-foreground">K</span> {m.k}</p>
                  <p><span className="font-bold text-foreground">A</span> {m.a}</p>
                  <p><span className="font-bold text-foreground">N/R</span> {m.nr}</p>
                  <p><span className="font-bold text-foreground">I</span> {m.i}</p>
                </div>
              </div>
            );
          })}
          <p className="pt-1 text-[11px] text-muted-foreground" data-testid="method-cadence-rule">
            <strong className="uppercase tracking-wide text-primary">Cadence rule</strong> — {content.cadence_rule}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Tiered leadership rhythm</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          {content.tiered_rhythm.map((t) => (
            <div key={t.label} className="rounded-lg border border-muted/60 p-3" data-testid={`method-tier-${t.label.split(' ')[0].toLowerCase()}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{t.label}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{t.text}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const AdoptionTab = ({ content }) => {
  const [data, setData] = useState(null);
  const [maturity, setMaturity] = useState(null);
  const [evidence, setEvidence] = useState({});

  const load = useCallback(async () => {
    setData(await getAdoption());
    setMaturity(await getMaturity());
  }, []);
  useEffect(() => { load(); }, [load]);

  const pass = async (phase) => {
    try { setData(await setAdoption({ phase, gate_passed: true, evidence: evidence[phase] || '' })); toast.success('Gate passed'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="method-adoption">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">90-day introduction path</CardTitle>
          <p className="text-xs text-muted-foreground">Phased practice with evidence gates — a gate passes on evidence, not on a date. {data ? `${data.gates_passed}/${data.total_phases} gates passed.` : ''}</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data?.phases || content.adoption_path).map((p) => (
            <div key={p.key} className={`rounded-lg border p-3 ${p.gate_passed ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-muted/60'}`} data-testid={`method-phase-${p.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-foreground">{p.phase} <span className="text-muted-foreground">· day {p.days}</span></span>
                {p.gate_passed ? <Badge className="bg-emerald-500/15 text-emerald-700 border border-emerald-500/40 text-[10px]">gate passed</Badge> : null}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground"><strong className="text-foreground">Practice</strong> {p.practice}</p>
              <p className="text-[11px] text-muted-foreground"><strong className="text-foreground">Evidence</strong> {p.evidence}</p>
              <p className="text-[11px] text-muted-foreground"><strong className="text-foreground">Gate</strong> {p.gate}</p>
              {!p.gate_passed && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Input value={evidence[p.key] || ''} onChange={(e) => setEvidence((s) => ({ ...s, [p.key]: e.target.value }))}
                         placeholder="Evidence for this gate" className="h-8 max-w-sm text-xs" data-testid={`method-phase-evidence-${p.key}`} />
                  <Button size="sm" variant="outline" onClick={() => pass(p.key)} data-testid={`method-phase-pass-${p.key}`}>Pass gate</Button>
                </div>
              )}
            </div>
          ))}
          <div className="rounded-lg border border-muted/60 p-3">
            <p className="text-xs font-semibold text-foreground">Implementation safeguards</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-[11px] text-muted-foreground">
              {content.implementation_safeguards.map((s) => <li key={s}>{s}</li>)}
            </ul>
            <p className="mt-2 text-[11px] text-primary" data-testid="method-scale-criterion">{content.scale_criterion}</p>
          </div>
        </CardContent>
      </Card>

      <LaunchCard content={content} />
      <MaturityCard content={content} maturity={maturity} onSaved={load} />

      {maturity?.history?.length > 1 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Maturity history</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {maturity.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid={`method-mat-history-${h.id}`}>
                <span>{(h.taken_at || '').slice(0, 16).replace('T', ' ')} · {h.taken_by}</span>
                <span className="font-semibold text-foreground">{h.score}/20 · {h.band}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const LibraryTab = ({ content }) => {
  const [findings, setFindings] = useState({});
  const exportPocket = async () => {
    try { await downloadPocketGuidePdf(); toast.success('PDF exported'); }
    catch (e) { toast.error(e.message); }
  };
  const review = async () => {
    try { await createBureaucracyReview({ findings }); toast.success('Review logged'); setFindings({}); }
    catch (e) { toast.error(e.message); }
  };
  const Table = ({ head, rows, tid }) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-xs" data-testid={tid}>
        <thead><tr className="text-muted-foreground">{head.map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-muted/60 align-top">
              {r.map((c, n) => <td key={n} className={`px-2 py-2 ${n === 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="method-library">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">The pocket guide</CardTitle>
            <p className="text-xs text-muted-foreground">
              Use this at the board, on the floor or during abnormality review. The PDF is watermarked 改善管理 and
              locked — printable and readable, not editable.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={exportPocket} data-testid="method-pocket-export">
            <Printer className="mr-1 h-4 w-4" />Export PDF
          </Button>
        </CardHeader>
        <CardContent>
          <Table head={['Move', 'Ask', 'Do', 'Prove']} tid="method-pocket-guide"
                 rows={content.pocket_guide.map((p) => [p.move, p.ask, p.do, p.prove])} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Why KANRI, and the gap it closes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {content.why_kanri.map((w) => (
              <div key={w.label} className="rounded-lg border border-muted/60 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{w.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{w.text}</p>
              </div>
            ))}
          </div>
          <Table head={['Without KANRI', 'The missing question', 'With KANRI']} tid="method-management-gap"
                 rows={content.management_gap.map((g) => [g.without, g.question, g.with])} />
          <div className="grid gap-2 sm:grid-cols-2">
            {content.loop_types.map((l) => (
              <div key={l.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-loop-${l.key}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{l.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{l.text}</p>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-muted/60 p-3">
            <p className="text-xs font-semibold text-foreground">Three truths</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-[11px] text-muted-foreground">
              {content.three_truths.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Role-based application</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Table head={['Role', 'Know', 'Align', 'Navigate / Respond', 'Integrate']} tid="method-roles"
                 rows={content.roles.map((r) => [r.role, r.know, r.align, r.navigate_respond, r.integrate])} />
          <div className="grid gap-2 sm:grid-cols-2">
            {content.good_looks_like.map((g) => (
              <div key={g.label} className="rounded-lg border border-muted/60 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{g.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{g.text}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">The human system</CardTitle>
          <p className="text-xs text-muted-foreground">Make problems safe to expose and hard to ignore.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {content.human_system.map((h) => (
              <div key={h.label} className="rounded-lg border border-muted/60 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{h.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{h.text}</p>
              </div>
            ))}
          </div>
          <Table head={['Use', 'Avoid']} tid="method-language"
                 rows={content.language_pairs.map((l) => [l.use, l.avoid])} />
          <p className="text-[11px] text-muted-foreground" data-testid="method-leadership-test">
            <strong className="uppercase tracking-wide text-primary">Leadership test</strong> — {content.leadership_test}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Translation into floor language</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Table head={['KANRI concept', 'Direct floor language', 'What it prevents']} tid="method-translation"
                 rows={content.translation.map((t) => [t.concept, t.floor, t.prevents])} />
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="method-promise">
            <p className="text-[11px] font-bold uppercase tracking-wide text-primary">The operating promise</p>
            <p className="text-[11px] text-foreground">{content.operating_promise}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {content.four_questions.map((q) => (
              <div key={q.label} className="rounded-lg border border-muted/60 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{q.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{q.text}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Digital abnormality response</CardTitle>
        </CardHeader>
        <CardContent>
          <Table head={['Move', 'Action', 'Technical evidence package']} tid="method-digital-response"
                 rows={content.digital_response.map((d) => [d.move, d.action, d.evidence])} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Anti-bureaucracy tests</CardTitle>
          <p className="text-xs text-muted-foreground">Tick what you recognise, then log the review. {content.removal_rule}</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.bureaucracy_tests.map((t) => (
            <label key={t.key} className="flex cursor-pointer items-start gap-3 rounded-lg border border-muted/60 p-3" data-testid={`method-bur-${t.key}`}>
              <input type="checkbox" className="mt-1" checked={!!findings[t.key]?.found}
                     onChange={(e) => setFindings((p) => ({ ...p, [t.key]: { found: e.target.checked, note: '' } }))}
                     data-testid={`method-bur-check-${t.key}`} />
              <div>
                <p className="text-xs font-semibold text-foreground">{t.signal}</p>
                <p className="text-[11px] text-muted-foreground">{t.test}</p>
                <p className="text-[11px] text-primary">→ {t.response}</p>
              </div>
            </label>
          ))}
          <Button size="sm" variant="outline" onClick={review} data-testid="method-bur-submit">Log review</Button>
        </CardContent>
      </Card>

      <FailureSignalsCard content={content} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Worked example · {content.worked_example.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {content.worked_example.initial.map((i) => (
              <div key={i.label} className="rounded-lg border border-muted/60 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{i.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{i.text}</p>
              </div>
            ))}
          </div>
          {content.worked_example.cycle.map((c) => (
            <div key={c.move} className="rounded-lg border border-muted/60 p-3">
              <p className="text-xs font-bold text-primary">{c.move}</p>
              <p className="text-[11px] text-foreground">{c.does}</p>
              <p className="text-[11px] text-muted-foreground">→ {c.output}</p>
              <p className="text-[11px] italic text-muted-foreground">“{c.floor}”</p>
            </div>
          ))}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
              <p className="text-[11px] font-bold uppercase text-rose-700">Before</p>
              <p className="text-[11px] text-muted-foreground">{content.worked_example.before}</p>
            </div>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <p className="text-[11px] font-bold uppercase text-emerald-700">After</p>
              <p className="text-[11px] text-muted-foreground">{content.worked_example.after}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <BrochureLibrary />
    </div>
  );
};

export default function MethodOsPage() {
  const [content, setContent] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [deptSource, setDeptSource] = useState(null);
  const [deptId, setDeptId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [today, setToday] = useState(null);
  const [tab, setTab] = useState('today');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try { setToday(await getToday(date, deptId)); } catch (e) { /* surfaced by the tabs */ }
  }, [date, deptId]);

  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([getMethodContent(), getMethodDepartments().catch(() => ({ departments: [] }))]);
        setContent(c);
        setDepartments(d.departments || []);
        setDeptSource(d);
        if ((d.departments || []).length) setDeptId(d.departments[0].id);
      } catch (e) { toast.error(e.message); }
      setLoading(false);
    })();
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const goToMove = (letter) => setTab({ K: 'know', A: 'align', N: 'navigate', R: 'respond', I: 'integrate' }[letter] || 'today');

  if (loading || !content) {
    return (
      <DashboardLayout>
        <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </DashboardLayout>
    );
  }

  const noDepartments = !departments.length;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5" data-testid="method-os-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground">
              <Compass className="h-5 w-5 text-primary" /> K.A.N.R.I. Method
            </h1>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-primary">{content.tagline}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!noDepartments && (
              <select value={deptId} onChange={(e) => setDeptId(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      data-testid="method-department-select">
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="h-9 w-40 text-sm" data-testid="method-date" />
          </div>
        </div>

        {noDepartments && (
          <Card className="border-amber-500/40 bg-amber-500/5" data-testid="method-no-departments">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-foreground">No departments configured yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                KANRI reads your areas from {deptSource?.source || 'FlowCrew™ → Departments'} so the condition board
                always matches the operation you actually run. Add them there first, then set your first condition here.
                The Learning tab explains every field before you start.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setTab('learning')} data-testid="method-go-learning">
                  Open Learning
                </Button>
                <Button size="sm" variant="outline" onClick={() => { window.location.href = '/planning-v2'; }}
                        data-testid="method-go-departments">
                  Go to FlowCrew™ Departments
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {content.moves.map((m) => (
            <button key={m.letter} onClick={() => goToMove(m.letter)}
                    className="rounded-xl border border-muted/60 bg-card p-3 text-left transition-colors hover:border-primary/50 cursor-pointer"
                    data-testid={`method-move-card-${m.letter}`}>
              <span className="font-display text-2xl font-bold text-primary/50">{m.letter}</span>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground">{m.name}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{m.question}</p>
            </button>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex w-full flex-wrap justify-start">
            {TABS.map(([k, label]) => (
              <TabsTrigger key={k} value={k} data-testid={`method-tab-${k}`}>{label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="today" className="mt-4"><TodayTab data={today} content={content} onGo={goToMove} /></TabsContent>
          <TabsContent value="learning" className="mt-4"><LearningTab content={content} onGo={setTab} /></TabsContent>
          <TabsContent value="know" className="mt-4"><KnowTab content={content} departments={departments} deptSource={deptSource} date={date} selectedDept={deptId} onSelectDept={setDeptId} onChanged={refresh} /></TabsContent>
          <TabsContent value="align" className="mt-4"><AlignTab content={content} date={date} deptId={deptId} onChanged={refresh} /></TabsContent>
          <TabsContent value="navigate" className="mt-4"><NavigateTab content={content} date={date} deptId={deptId} onChanged={refresh} /></TabsContent>
          <TabsContent value="respond" className="mt-4"><RespondTab content={content} date={date} deptId={deptId} onChanged={refresh} /></TabsContent>
          <TabsContent value="integrate" className="mt-4"><IntegrateTab content={content} date={date} deptId={deptId} onChanged={refresh} /></TabsContent>
          <TabsContent value="rhythm" className="mt-4"><RhythmTab content={content} date={date} deptId={deptId} onChanged={refresh} /></TabsContent>
          <TabsContent value="management" className="mt-4"><ManagementTab content={content} date={date} /></TabsContent>
          <TabsContent value="metrics" className="mt-4"><MetricsTab content={content} /></TabsContent>
          <TabsContent value="adoption" className="mt-4"><AdoptionTab content={content} /></TabsContent>
          <TabsContent value="library" className="mt-4"><LibraryTab content={content} /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
